// Staff sign-in on the shop. There is no sign-in form here: a person signed in to Manager is handed over with a
// one-time code in the address (#signin=<code>), which is swapped for a Store session kept in this browser.
// A device that has been signed in before is quietly re-linked through Manager when its session runs out.
const TOKEN_KEY = '3dlabs.storeSession';
const STAFF_KEY = '3dlabs.storeStaff';
const MANAGER_TOKEN_KEY = '3dlabs.sessionToken';
const BOUNCE_KEY = '3dlabs.storeLinkTried';

const read = (storage, key) => { try { return storage.getItem(key); } catch { return null; } };
const write = (storage, key, value) => { try { if (value === null) storage.removeItem(key); else storage.setItem(key, value); } catch { /* private mode */ } };

let token = null;
export const storeToken = () => token;

// Pictures and previews the editor shows from private addresses (drafts, new uploads) carry a media pass, never the
// Store sign-in (T23): it opens only those pictures, lasts an hour and ends with the sign-in. Renewed 15 minutes before
// it runs out; each new one fires "3dlabs-store-media-pass" so the editor redraws with it.
let pass = null, passTimer = null;
export const storeMediaPass = () => (pass && pass.expiresAt - Date.now() > 60000 ? pass.value : '');
async function refreshStorePass() {
  clearTimeout(passTimer);
  if (!token) { pass = null; return null; }
  try {
    const result = await staffApi('/auth/media-pass', { method: 'POST', body: {} });
    pass = { value: result.pass, expiresAt: Date.parse(result.expires_at) };
    try { window.dispatchEvent(new Event('3dlabs-store-media-pass')); } catch { /* not in a browser */ }
  } catch { pass = null; }
  passTimer = setTimeout(refreshStorePass, pass ? Math.max(60000, pass.expiresAt - Date.now() - 15 * 60000) : 60000);
  return pass;
}

const bare = (address) => String(address || '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '').toLowerCase();
// Where Manager lives, seen from this page. Only on the public Store address is it somewhere else; in the Windows
// app, on the host and on the local network both are served from the one address, so the answer is '' (stay here).
export const managerAddress = (addresses = {}) => addresses.manager_url && addresses.store_url && bare(location.host) === bare(addresses.store_url) && bare(addresses.manager_url) !== bare(addresses.store_url) ? addresses.manager_url : '';

async function call(path, options = {}) {
  const headers = { ...(options.body ? { 'content-type': 'application/json' } : {}), ...(options.token ? { authorization: `Bearer ${options.token}` } : {}) };
  const response = await fetch(`/api${path}`, { method: options.method || 'GET', headers, body: options.body ? JSON.stringify(options.body) : undefined });
  if (!response.ok) throw Object.assign(new Error('Request failed'), { status: response.status });
  return response.json();
}
const whoAmI = (candidate) => call('/store/session', { token: candidate }).then((who) => (who.can?.manage || who.can?.analytics ? { ...who, token: candidate } : null));

// Resolves to { user, can, token } for signed-in staff, or null for everyone else. Never shows anything to a customer.
export async function startStoreSession(addresses = {}) {
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const code = hash.get('signin');
  if (code !== null) {
    hash.delete('signin');
    history.replaceState(history.state, '', location.pathname + location.search + (hash.toString() ? `#${hash}` : ''));
  }
  let session = null;
  if (code && code !== 'none') {
    try { const result = await call('/public/store/session', { method: 'POST', body: { code } }); session = result.can?.manage || result.can?.analytics ? result : null; } catch { session = null; }
  }
  if (!session) {
    // A saved Store session, or (on the host itself and the local network, where both sites share one address) Manager's own.
    for (const candidate of [read(localStorage, TOKEN_KEY), read(localStorage, MANAGER_TOKEN_KEY)]) {
      if (!candidate || session) continue;
      try { session = await whoAmI(candidate); } catch { session = null; }
    }
  }
  if (session) {
    token = session.token;
    if (read(localStorage, MANAGER_TOKEN_KEY) !== token) write(localStorage, TOKEN_KEY, token);
    write(localStorage, STAFF_KEY, '1');
    await refreshStorePass();
    return session;
  }
  write(localStorage, TOKEN_KEY, null);
  // Re-link once per visit, and only on a device that has been staff before, so customers are never sent anywhere.
  const manager = managerAddress(addresses);
  if (code === null && read(localStorage, STAFF_KEY) === '1' && !read(sessionStorage, BOUNCE_KEY) && manager) {
    write(sessionStorage, BOUNCE_KEY, '1');
    location.replace(`${manager}/?store-link=${encodeURIComponent(location.pathname)}`);
    return new Promise(() => {});
  }
  if (code === 'none') write(sessionStorage, BOUNCE_KEY, '1');
  return null;
}

export async function endStoreSession() {
  const current = token; token = null; pass = null; clearTimeout(passTimer);
  write(localStorage, TOKEN_KEY, null); write(localStorage, STAFF_KEY, null);
  // Only a Store session is ended here; a Manager session on a shared address is left alone.
  if (current && read(localStorage, MANAGER_TOKEN_KEY) !== current) { try { await call('/auth/sign-out', { method: 'POST', token: current }); } catch { /* already gone */ } }
}

// Signed requests for the shop editor.
export async function staffApi(path, options = {}) {
  if (!token) throw Object.assign(new Error('Not signed in.'), { status: 401 });
  const response = await fetch(`/api${path}`, {
    method: options.method || 'GET',
    headers: { authorization: `Bearer ${token}`, ...(options.body && !(options.body instanceof FormData) ? { 'content-type': 'application/json' } : {}) },
    body: options.body instanceof FormData ? options.body : options.body ? JSON.stringify(options.body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(payload.error || 'Something went wrong.'), { status: response.status, payload });
  return payload;
}

// Uploads one file in pieces (the host accepts up to 8 MB at a time) and returns its file record.
export async function uploadFile(file, onProgress = () => {}) {
  if (!token) throw Object.assign(new Error('Not signed in.'), { status: 401 });
  const start = await staffApi('/uploads/start', { method: 'POST', body: { filename: file.name, size: file.size, record_type: 'general', client_key: `shop-${file.name}-${file.size}-${file.lastModified}` } });
  // Already uploaded once (same name, size and date): reuse it.
  if (start.status === 'complete' && start.file_id) return { id: start.file_id, original_name: file.name, mime_type: file.type };
  const piece = 6 * 1024 * 1024;
  for (let offset = Number(start.received_size || 0); offset < file.size; offset += piece) {
    const response = await fetch(`/api/uploads/${start.id}/chunk`, { method: 'PUT', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/octet-stream', 'x-upload-offset': String(offset) }, body: file.slice(offset, offset + piece) });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'The upload was interrupted. Try again.');
    onProgress(Math.min(1, (offset + piece) / file.size));
  }
  return (await staffApi(`/uploads/${start.id}/complete`, { method: 'POST', body: {} })).file;
}
